<template>
    <tab-layout>
        <!-- TAB TITLE -->
        <template #title>
            {{ $l10n.__('Informations Générales') }}
        </template>

        <!-- TAB CONTENT -->
        <template #content>
            <div :class="mainClasses" data-bot="overview__general-info">
                <!-- GENERAL -->
                <section class="flex">
                    <h3 class="m-3 flex-row baseline" v-html="isCorporationLabel"></h3>

                    <div class="px-3 br-1">
                        <div>
                            <h4 class="m-0">{{ $l10n.__('Générales') }}</h4>
                            <ul class="my-2">
                                <li
                                    v-for="(item, indexItem) in removeInvalidValue(general)"
                                    :key="indexItem"
                                    class="data-block data-block-hover"
                                >
                                    <!-- Dynamic load data component -->
                                    <component
                                        :is="item.format + '-data'"
                                        v-bind="item"
                                        :useFavorite="!!item.favoriteName"
                                        :list="favorites"
                                        @toggleFavorite="updateFavorites(item)"
                                    />
                                </li>
                            </ul>
                        </div>

                        <!-- CONTACT -->
                        <div v-if="mainContact && identity.isCorporation" class="mt-4">
                            <h4 class="m-0">{{ $l10n.__('Contact principal') }}</h4>
                            <ul class="my-2">
                                <li
                                    v-for="(item, indexItem) in removeInvalidValue(mainContact)"
                                    :key="indexItem"
                                    class="data-block data-block-hover"
                                >
                                    <!-- Dynamic load data component -->
                                    <component
                                        :is="item.format + '-data'"
                                        v-bind="item"
                                        :useFavorite="!!item.favoriteName"
                                        :list="favorites"
                                        @toggleFavorite="updateFavorites(item)"
                                    />
                                </li>
                            </ul>
                        </div>
                    </div>
                </section>

                <!-- SLOT 2 -->
                <section class="flex">
                    <h3 class="m-3 flex-row baseline">
                        <span class="flex-right">
                            <CompassTooltip
                                :disabled="canEdit"
                                placement="bottom-end"
                                :content="
                                    $t.__('common.labels.no_permissions_to_perform_this_action')
                                "
                            >
                                <CompassButton
                                    :label="$t.__('overview.tabs.TabInformationsThird.edit')"
                                    type="tertiary"
                                    :disabled="!canEdit"
                                    :data-bot="databot['generalinfoedit1']"
                                    @click="edit"
                                />
                            </CompassTooltip>
                        </span>
                    </h3>

                    <div class="px-3">
                        <!-- ACCOUNTING -->
                        <div v-if="removeRateCategoryForSupplier(financial).length > 0">
                            <h4 class="m-0">{{ $l10n.__('Données comptables') }}</h4>
                            <ul class="my-2">
                                <li
                                    v-for="(item, indexItem) in removeRateCategoryForSupplier(
                                        financial,
                                    )"
                                    :key="indexItem"
                                    class="data-block data-block-hover"
                                >
                                    <!-- Dynamic load data component -->
                                    <component
                                        :is="item.format + '-data'"
                                        v-bind="item"
                                        :useFavorite="!!item.favoriteName"
                                        :list="favorites"
                                        @toggleFavorite="updateFavorites(item)"
                                    />
                                </li>
                            </ul>
                        </div>

                        <!-- LEGAL -->
                        <div
                            v-if="removeInvalidValue(legal).length > 0 && identity.isCorporation"
                            class="mt-4"
                        >
                            <h4 class="m-0">{{ $l10n.__('Informations juridiques') }}</h4>
                            <ul class="my-2">
                                <li
                                    v-for="(item, indexItem) in removeInvalidValue(legal)"
                                    :key="indexItem"
                                    class="data-block data-block-hover"
                                >
                                    <!-- Dynamic load data component -->
                                    <component
                                        :is="item.format + '-data'"
                                        v-bind="item"
                                        :useFavorite="true"
                                        :list="favorites"
                                        @toggleFavorite="updateFavorites(item)"
                                    />
                                </li>
                            </ul>
                        </div>
                    </div>
                </section>

                <!-- SELLSY -->
                <sellsy-informations-section
                    :linkedid="$store.getters.linkedid"
                    :linkedtype="$store.getters.linkedtype"
                    :enableTags="true"
                    :data="$store.getters.sellsyInformations"
                    :canDisplayMassMailingConsent="canDisplayMassMailingConsent"
                />
            </div>

            <MarketingConsentOverview v-if="canDisplayMarketingConsent" />
        </template>
    </tab-layout>
</template>

<script>
import TabLayout from 'vcomponents/overview/tabs/TabLayout.vue';
import SellsyInformationsSection from './SellsyInformationsSection.vue';
import MarketingConsentOverview from 'vcomponents/marketing/consent/MarketingConsentOverview.vue';
import Text from './data/Text.vue';
import Textarea from './data/Textarea.vue';
import Link from './data/Link.vue';
import ExternalLink from './data/ExternalLink.vue';
import Phone from './data/Phone.vue';
import Email from './data/Email.vue';
import { buildUrl } from '../../../helpers';

export default {
    components: {
        'sellsy-informations-section': SellsyInformationsSection,
        'text-data': Text,
        'textarea-data': Textarea,
        'link-data': Link,
        'external_url-data': ExternalLink,
        'phone-data': Phone,
        'email-data': Email,
        TabLayout,
        MarketingConsentOverview,
    },
    computed: {
        identity() {
            return this.$store.getters.identity;
        },
        general() {
            return this.$store.getters.informations;
        },
        mainContact() {
            return this.$store.getters.contacts;
        },
        financial() {
            return this.$store.getters.financial;
        },
        legal() {
            return this.$store.getters.legal;
        },
        favorites() {
            return this.$store.getters.favorites;
        },
        routes() {
            return this.$store.getters.routes;
        },
        supplierInvalidRateCategories() {
            return ['financial_rateCategory'];
        },
        isCorporationLabel() {
            return this.identity.isCorporation
                ? this.$l10n.__('La société')
                : this.$l10n.__('Le particulier');
        },
        databot() {
            return this.$store.getters.databots;
        },
        permissions() {
            return this.$store.getters.permissions;
        },
        canEdit() {
            return this.permissions ? this.permissions['can_edit'] : true;
        },
        mainClasses() {
            return ['well p-0 pb-3 flex-level flex-top ovx-auto overview__general-info'];
        },
        canDisplayMarketingConsent() {
            return this.permissions.can_show_rgpd_consents;
        },
        canDisplayMassMailingConsent() {
            return this.permissions.can_show_campaign_subscription;
        },
    },
    methods: {
        buildUrl,
        updateFavorites(item) {
            const thirdRoute = this.routes.find((route) => route.id === 'favorite');
            if (thirdRoute) {
                this.$store.dispatch('updateFavorites', {
                    item: item,
                    route: this.buildUrl(thirdRoute.value, [
                        { key: 'type', value: this.$store.getters.identity.type },
                    ]),
                });
            }
        },
        removeRateCategoryForSupplier(list) {
            if (this.identity.type === 'supplier') {
                list = list.filter(
                    (item) => !this.supplierInvalidRateCategories.includes(item.favoriteName),
                );
            }
            return this.removeInvalidValue(list);
        },
        removeInvalidValue(list) {
            return list && list.length > 0
                ? list.filter((item) => item.value && item.value !== '')
                : [];
        },
        edit() {
            window.Third.edit(this.$store.getters.linkedid);
        },
    },
};
</script>
<style lang="scss" scoped>
.overview__general-info {
    flex: 1 0 auto;
}
</style>
